import { useEffect, useMemo, useState } from 'react';
import {
  PortalDsAlert,
  PortalDsCard,
  PortalDsInput,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';

export interface ReferenceAccount {
  account_id: string;
  account_name: string;
}

export interface ReferenceCampaign {
  campaign_id: string;
  campaign_name: string;
}

interface ClientSqlReferencePanelProps {
  accounts: ReferenceAccount[];
  campaigns: ReferenceCampaign[];
  isLoading?: boolean;
  hasClient: boolean;
  errorMessage?: string | null;
  onInsertId?: (id: string) => void;
}

type ReferenceTab = 'accounts' | 'campaigns';

function filterAccounts(accounts: ReferenceAccount[], query: string): ReferenceAccount[] {
  const normalised = query.trim().toLowerCase();
  if (!normalised) {
    return accounts;
  }
  return accounts.filter((account) => (
    account.account_id.toLowerCase().includes(normalised)
    || account.account_name.toLowerCase().includes(normalised)
  ));
}

function filterCampaigns(campaigns: ReferenceCampaign[], query: string): ReferenceCampaign[] {
  const normalised = query.trim().toLowerCase();
  if (!normalised) {
    return campaigns;
  }
  return campaigns.filter((campaign) => (
    campaign.campaign_id.toLowerCase().includes(normalised)
    || campaign.campaign_name.toLowerCase().includes(normalised)
  ));
}

export function ClientSqlReferencePanel({
  accounts,
  campaigns,
  isLoading = false,
  hasClient,
  errorMessage = null,
  onInsertId,
}: ClientSqlReferencePanelProps) {
  const [tab, setTab] = useState<ReferenceTab>('accounts');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setQuery('');
    setTab('accounts');
  }, [hasClient]);

  const filteredAccounts = useMemo(
    () => filterAccounts(accounts, query),
    [accounts, query]
  );
  const filteredCampaigns = useMemo(
    () => filterCampaigns(campaigns, query),
    [campaigns, query]
  );

  return (
    <PortalDsCard className="space-y-4">
      <PortalDsSectionHeader
        title="Client reference"
        description="Accounts and campaigns associated with this client — click an ID to insert it into the SQL editor at your cursor."
      />

      {!hasClient ? (
        <p className="text-sm text-gray-500">
          Select a client to load account and campaign IDs.
        </p>
      ) : errorMessage ? (
        <PortalDsAlert variant="danger">
          {errorMessage}
        </PortalDsAlert>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">
          Loading reference data…
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={tab === 'accounts'
                ? 'rounded border border-black bg-black px-3 py-1.5 text-sm font-medium text-white'
                : 'rounded border border-gray-400 bg-white px-3 py-1.5 text-sm font-medium text-black'}
              onClick={() => setTab('accounts')}
            >
              Accounts
              {' '}
              (
              {accounts.length}
              )
            </button>
            <button
              type="button"
              className={tab === 'campaigns'
                ? 'rounded border border-black bg-black px-3 py-1.5 text-sm font-medium text-white'
                : 'rounded border border-gray-400 bg-white px-3 py-1.5 text-sm font-medium text-black'}
              onClick={() => setTab('campaigns')}
            >
              Campaigns
              {' '}
              (
              {campaigns.length}
              )
            </button>
            <div className="min-w-[220px] flex-1">
              <PortalDsInput
                value={query}
                placeholder={tab === 'accounts' ? 'Filter accounts…' : 'Filter campaigns…'}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>

          <div className={`${portalDs.tableContainer} max-h-64 overflow-auto`}>
            {tab === 'accounts' ? (
              <table className="min-w-full">
                <thead className={portalDs.tableHead}>
                  <tr>
                    <th className={portalDs.tableHeadCell}>
                      Account ID
                    </th>
                    <th className={portalDs.tableHeadCell}>
                      Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-4 py-4 text-sm text-gray-500"
                      >
                        {accounts.length === 0
                          ? 'No accounts are associated with this client.'
                          : 'No accounts match this filter.'}
                      </td>
                    </tr>
                  ) : (
                    filteredAccounts.map((account) => (
                      <tr
                        key={account.account_id}
                        className={portalDs.tableBodyRow}
                      >
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            title="Insert into SQL editor"
                            className="rounded px-1.5 py-0.5 font-mono text-xs text-gray-800 hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black"
                            onClick={() => onInsertId?.(account.account_id)}
                          >
                            {account.account_id}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {account.account_name || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full">
                <thead className={portalDs.tableHead}>
                  <tr>
                    <th className={portalDs.tableHeadCell}>
                      Campaign ID
                    </th>
                    <th className={portalDs.tableHeadCell}>
                      Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCampaigns.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-4 py-4 text-sm text-gray-500"
                      >
                        {campaigns.length === 0
                          ? 'No campaigns are associated with this client.'
                          : 'No campaigns match this filter.'}
                      </td>
                    </tr>
                  ) : (
                    filteredCampaigns.map((campaign) => (
                      <tr
                        key={campaign.campaign_id}
                        className={portalDs.tableBodyRow}
                      >
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            title="Insert into SQL editor"
                            className="rounded px-1.5 py-0.5 font-mono text-xs text-gray-800 hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black"
                            onClick={() => onInsertId?.(campaign.campaign_id)}
                          >
                            {campaign.campaign_id}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {campaign.campaign_name || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </PortalDsCard>
  );
}
